// @flow
import { get, isEmpty } from 'lodash';
import shortId from 'shortid';
import {
  compact,
  filter,
  flatten,
  isNumber,
  identity,
  map,
  path,
  pipe,
  sortBy,
  sortedUniq,
  uniq,
  uniqBy,
} from 'lodash/fp';

import {
  CABIN_CLASS_CATEGORY,
  CABIN_CLASS_BUSINESS,
  CABIN_CLASS_ECO,
  COMPANIES_CATEGORY,
  LAYOVERS_CATEGORY,
  TIME_RANGES_CATEGORY,
  ZERO_LAYOVERS_ICON,
  ONE_LAYOVERS_ICON,
  TWO_LAYOVERS_ICON,
} from 'Utils/transport/additionalTransports.constants';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';

type Company = {
  id: string,
  label: string,
  logo: string,
};

function geIntValue(time: TimeRangeValue): number {
  return time.hours * 60 + time.minutes;
}
function isTimeBefore(time: TimeRangeValue, refTime: TimeRangeValue): boolean {
  return geIntValue(time) < geIntValue(refTime);
}

function isTimeAfter(time: TimeRangeValue, refTime: TimeRangeValue): boolean {
  return geIntValue(time) >= geIntValue(refTime);
}

function isTimeInRange(time: TimeRangeValue): TimeRange => boolean {
  return (timeRange: TimeRange) =>
    isTimeAfter(time, timeRange.start) && isTimeBefore(time, timeRange.end);
}

function timeRangeValueToString({ hours, minutes }: TimeRangeValue): string {
  return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
}

function timeRangeToString(time: TimeRange): string {
  return `${timeRangeValueToString(time.start)} - ${timeRangeValueToString(time.end)}`;
}

const STRICT_EQUALITY_MATCHER = (a: *, b: *): boolean => a === b;
const ARRAY_INCLUSION_MATCHER = (array: Array<*>, item: *): boolean => array.includes(item);
const COMPANIES_MATCHER = (companies: Array<Company>, companyId: string): boolean =>
  !!companies.find(c => c.id === companyId);
const TIME_RANGE_MATCHER = (time: TimeRangeValue, timeRange: TimeRange): boolean =>
  isTimeInRange(time)(timeRange);

function createCompaniesFilters(
  transports: Array<AdditionalTransport>,
  // $FlowFixMe
): (Array<*>, $Shape<Filter>) => Array<Filter> {
  const companies = pipe([map(path('filterValues.companies')), flatten, compact, uniqBy('id')])(
    transports,
  );

  return companies.map(company => {
    return {
      id: shortId.generate(),
      category: COMPANIES_CATEGORY,
      match: COMPANIES_MATCHER,
      meta: {
        imageUrl: company.logo,
        label: company.label,
      },
      path: 'filterValues.companies',
      selected: false,
      value: company.id,
    };
  });
}

function getLayoversInfo(i18n: *): number => { icon: IconName, label: string } {
  return (layovers: number) => {
    switch (layovers) {
      case 0:
        return { label: get(i18n, 'filters.layovers.direct'), icon: ZERO_LAYOVERS_ICON };

      case 1:
        return {
          label: `${layovers} ${get(i18n, 'filters.layovers.stop.label_1')}`,
          icon: ONE_LAYOVERS_ICON,
        };

      default:
        return {
          label: `${layovers} ${get(i18n, 'filters.layovers.stop.label_n')}`,
          icon: TWO_LAYOVERS_ICON,
        };
    }
  };
}

function createLayoversFilters(transports: Array<AdditionalTransport>, i18n: *): Array<Filter> {
  const layoversInfo = getLayoversInfo(i18n);
  const layovers: Array<number> = pipe([
    map(path('filterValues.layovers')),
    filter(isNumber),
    sortBy(identity),
    sortedUniq,
  ])(transports);

  return layovers.map(layover => {
    const { label, icon } = layoversInfo(layover);
    return {
      id: shortId.generate(),
      category: LAYOVERS_CATEGORY,
      match: STRICT_EQUALITY_MATCHER,
      meta: {
        icon,
        label,
      },
      path: 'filterValues.layovers',
      selected: false,
      value: layover,
    };
  });
}

function getTimeRangeTransformations(timeRanges: Array<TimeRange>): string => Array<*> {
  return pathValue => {
    function getTimeRange(time?: TimeRangeValue): ?TimeRange {
      return time && timeRanges.find(isTimeInRange(time));
    }

    return [
      map(path(pathValue)),
      map(getTimeRange),
      compact,
      uniq,
      sortBy(timeRange => timeRange.start.hours),
    ];
  };
}

function createTimeRangeFilters(
  transports: Array<AdditionalTransport>,
  timeRanges: Array<TimeRange>,
): string => Array<Filter> {
  return pathValue => {
    const values = pipe(getTimeRangeTransformations(timeRanges)(pathValue))(transports);

    return values.map(value => ({
      id: shortId.generate(),
      category: TIME_RANGES_CATEGORY,
      match: TIME_RANGE_MATCHER,
      meta: {
        icon: value.icon,
        label: value.label,
        subLabel: timeRangeToString(value),
      },
      path: pathValue,
      selected: false,
      value,
    }));
  };
}

function createCabinClassFilters(transports: Array<AdditionalTransport>, i18n: *): Array<Filter> {
  const cabinClassPath = 'filterValues.cabinClasses';
  const cabinClasses = pipe([map(path('filterValues.cabinClasses')), compact])(transports);

  return !isEmpty(cabinClasses)
    ? [
        {
          id: shortId.generate(),
          category: CABIN_CLASS_CATEGORY,
          match: ARRAY_INCLUSION_MATCHER,
          meta: {
            label: get(i18n, 'filters.cabinClass.economy'),
          },
          path: cabinClassPath,
          selected: false,
          value: CABIN_CLASS_ECO, // temporairement en dur car on ne récupère que des ECO pour les vols
        },
        {
          id: shortId.generate(),
          category: CABIN_CLASS_CATEGORY,
          match: ARRAY_INCLUSION_MATCHER,
          meta: {
            label: get(i18n, 'filters.cabinClass.business'),
          },
          path: cabinClassPath,
          selected: false,
          value: CABIN_CLASS_BUSINESS,
        },
      ]
    : [];
}

function createFilters(
  transports: Array<AdditionalTransport>,
  timeRanges: Array<TimeRange>,
  i18n: *,
): Array<Filter> {
  const timeRangeFilters = createTimeRangeFilters(transports, timeRanges);

  return [
    ...createCabinClassFilters(transports, i18n),
    ...createCompaniesFilters(transports),
    ...createLayoversFilters(transports, i18n),
    ...timeRangeFilters('filterValues.inboundDepartureTime'),
    ...timeRangeFilters('filterValues.inboundArrivalTime'),
    ...timeRangeFilters('filterValues.outboundDepartureTime'),
    ...timeRangeFilters('filterValues.outboundArrivalTime'),
  ];
}

function unselectCategory(filters: Array<Filter>): string => Array<Filter> {
  return category => {
    return filters.map(f => ({
      ...f,
      selected: f.category === category ? false : f.selected,
    }));
  };
}

function unselectFilters(filters: Array<Filter>, filtersToDisable: Array<Filter>): Array<Filter> {
  return filters.map(f => {
    const filterToDisable = filtersToDisable.find(({ id }) => id === f.id);
    return {
      ...f,
      selected: filterToDisable ? false : f.selected,
    };
  });
}

export {
  ARRAY_INCLUSION_MATCHER,
  COMPANIES_MATCHER,
  STRICT_EQUALITY_MATCHER,
  TIME_RANGE_MATCHER,
  isTimeInRange,
  unselectFilters,
  createFilters,
  unselectCategory,
};
